import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Tag, message } from 'antd';
import { AcceptanceRun, acceptanceRunApi } from '../../services/acceptanceRunApi';
import { AcceptanceManifest } from './types';

const storageKey = (formId: string) => `erp.acceptance.active-run.${formId}`;

export const getActiveAcceptanceRun = (formId: string): AcceptanceRun | null => {
  try {
    const value = localStorage.getItem(storageKey(formId));
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const AcceptanceRunControls: React.FC<{
  manifest: AcceptanceManifest;
  documentKey?: string;
  canPass: boolean;
  evidenceSummary: unknown;
}> = ({ manifest, documentKey, canPass, evidenceSummary }) => {
  const [run, setRun] = useState<AcceptanceRun | null>(() => getActiveAcceptanceRun(manifest.formId));
  const [note, setNote] = useState('');
  const [result, setResult] = useState<'pass' | 'fail' | 'cancelled'>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (run?.status !== 'in_progress') {
      localStorage.removeItem(storageKey(manifest.formId));
    }
  }, [manifest.formId, run]);

  const start = async () => {
    setLoading(true);
    try {
      const created = await acceptanceRunApi.start({
        formId: manifest.formId,
        chainId: manifest.chainId,
        contractHash: manifest.contractHash,
        documentKey,
      });
      localStorage.setItem(storageKey(manifest.formId), JSON.stringify(created));
      setRun(created);
      message.success(`驗收案例 ${created.runId} 已開始`);
    } catch {
      message.error('開始驗收案例失敗');
    } finally {
      setLoading(false);
    }
  };

  const complete = async () => {
    if (!run || !result) return;
    setLoading(true);
    try {
      const completed = await acceptanceRunApi.complete(run.runId, result, note.trim(), evidenceSummary);
      localStorage.removeItem(storageKey(manifest.formId));
      setRun(completed);
      setResult(undefined);
      setNote('');
      message.success(`驗收案例已標記 ${completed.status}`);
    } catch {
      message.error('結束驗收案例失敗');
    } finally {
      setLoading(false);
    }
  };

  return <Space wrap style={{ marginBottom: 12 }}>
    {!run || run.status !== 'in_progress'
      ? <Button type="primary" loading={loading} onClick={() => { void start(); }}>開始驗收案例</Button>
      : <>
        <Tag color="blue">執行中：{run.runId}</Tag>
        <Button type="primary" disabled={!canPass} onClick={() => setResult('pass')}>PASS</Button>
        <Button danger onClick={() => setResult('fail')}>FAIL</Button>
        <Button onClick={() => setResult('cancelled')}>取消案例</Button>
        {!canPass && <Tag color="red">觀測器未全綠或缺口尚未知情確認，不能 PASS</Tag>}
      </>}
    <Modal
      title={`結束驗收案例：${result || ''}`}
      open={!!result}
      onCancel={() => setResult(undefined)}
      onOk={() => { void complete(); }}
      confirmLoading={loading}
      okButtonProps={{ disabled: result === 'fail' && !note.trim() }}
      okText="確認結束"
    >
      <Input.TextArea
        rows={4}
        value={note}
        onChange={event => setNote(event.target.value)}
        placeholder={result === 'fail' ? 'FAIL 必須說明錯誤或期望行為' : '補充人工驗收說明'}
      />
    </Modal>
  </Space>;
};
